const mapStation = (s) => ({
	id: s.id,
	code: s.code,
	fullname: s.name,
	arrival: s.arrival,
	departure: s.departure,
	timezone: s.timezone,
});

const createBreakdownIndex = (sequences) => {
	const byGroup = new Map();
	const stationIds = new Set();
	const breakdownSequences = [];

	for (const seq of sequences) {
		if (!seq.breakdown) continue;
		breakdownSequences.push(seq);
		if (seq.sequenceGroupId) {
			byGroup.set(seq.sequenceGroupId, seq.breakdown);
		}
		if (seq.breakdown.station?.id) {
			stationIds.add(seq.breakdown.station.id);
		}
	}

	return { byGroup, stationIds, breakdownSequences };
};

const normalizeSequences = (sequences, stationList) =>
	sequences
		.map((seq) => {
			const fromIdx = stationList.findIndex((s) => s.id === seq.fromStation.id);
			const toIdx = stationList.findIndex((s) => s.id === seq.toStation.id);
			return { ...seq, fromIdx, toIdx };
		})
		.filter((s) => s.fromIdx !== -1 && s.toIdx !== -1 && s.toIdx > s.fromIdx)
		.sort((a, b) => a.fromIdx - b.fromIdx);

const resolveSegmentBreakdown = (seq, segmentStations, prevLastStationId, index) => {
	const { byGroup, stationIds, breakdownSequences } = index;

	if (seq.breakdown) return seq.breakdown;

	const groupBreakdown = byGroup.get(seq.sequenceGroupId);
	if (groupBreakdown) return groupBreakdown;

	for (let i = 0; i < segmentStations.length; i++) {
		const stationId = segmentStations[i].id;
		if (i === 0 && stationId === prevLastStationId) continue;
		if (stationIds.has(stationId)) {
			const match = breakdownSequences.find((s) => s.breakdown?.station?.id === stationId);
			if (match) return match.breakdown;
		}
	}

	return resolveByTimestamp(segmentStations, breakdownSequences);
};

const resolveByTimestamp = (segmentStations, breakdownSequences) => {
	const from = segmentStations[0];
	const to = segmentStations[segmentStations.length - 1];
	if (!from?.departure || !to?.arrival) return null;

	for (const bdSeq of breakdownSequences) {
		if (!bdSeq.toTimestamp) continue;
		if (bdSeq.toTimestamp >= from.departure && bdSeq.toTimestamp <= to.arrival) {
			return bdSeq.breakdown;
		}
	}
	return null;
};

const createGapSegments = (fromIdx, toIdx, stationList, breakdownSequences) => {
	const gaps = [];
	for (let i = fromIdx; i < toIdx; i++) {
		const gapStations = [stationList[i], stationList[i + 1]];
		const breakdown = resolveByTimestamp(gapStations, breakdownSequences);
		gaps.push({
			stations: gapStations.map(mapStation),
			breakdown,
			isGap: true,
		});
	}
	return gaps;
};

const createSequenceSegment = (seq, stationList, prevLastStationId, index) => {
	const segmentStations = stationList.slice(seq.fromIdx, seq.toIdx + 1);
	const breakdown = resolveSegmentBreakdown(seq, segmentStations, prevLastStationId, index);

	return {
		sequenceGroupId: seq.sequenceGroupId,
		stations: segmentStations.map(mapStation),
		crewMember: seq.crewMember,
		status: seq.status,
		activityType: seq.activityType,
		extraActivities: seq.extraActivities ?? [],
		fromTimestamp: seq.fromTimestamp,
		isMultiRide: seq.isMultiRide,
		job: [seq.job].filter(Boolean),
		run: [seq.run].filter(Boolean),
		toTimestamp: seq.toTimestamp,
		breakdown,
		isGap: false,
	};
};

/**
 * Builds display segments from workAssignmentSequences + ride stations.
 * Each sequence covers fromStation→toStation (inclusive slice of the stations array).
 * Gaps between sequences (and the trailing remainder) become unassigned segments.
 *
 * Returns an array of segment objects:
 *   { stations[], crewMember, status, job, run, activityType, isMultiRide, isGap }
 */
export const buildSegments = (stationList, sequences) => {
	if (!stationList?.length) return [];

	const allSequences = sequences || [];
	const index = createBreakdownIndex(allSequences);
	const normalized = normalizeSequences(allSequences, stationList);

	const segments = [];
	let cursor = 0;
	let prevLastStationId = null;

	for (const seq of normalized) {
		if (seq.fromIdx > cursor) {
			const gaps = createGapSegments(cursor, seq.fromIdx, stationList, index.breakdownSequences);
			segments.push(...gaps);
			prevLastStationId = stationList[seq.fromIdx - 1]?.id ?? null;
		}

		segments.push(createSequenceSegment(seq, stationList, prevLastStationId, index));
		prevLastStationId = stationList[seq.toIdx]?.id ?? null;
		cursor = seq.toIdx;
	}

	if (cursor < stationList.length - 1) {
		const gaps = createGapSegments(
			cursor,
			stationList.length - 1,
			stationList,
			index.breakdownSequences
		);
		segments.push(...gaps);
	}

	return segments;
};

export const generateAssignmentsMap = (stationList, workAssigments) => {
	const segmentMap = new Map();

	for (let i = 0; i < stationList.length - 1; i++) {
		segmentMap.set(stationList[i].id, {
			storeId: stationList[i].id,
			isLoading: false,
			fromStationId: stationList[i].id,
			toStationId: stationList[i + 1].id,
			departure: stationList[i].departure,
			arrival: stationList[i + 1].arrival,
			crewMember: null,
			status: null,
			version: null,
			id: null,
		});
	}

	workAssigments.forEach((assigment) => {
		const stationId = assigment.fromStation.id;
		if (segmentMap.has(stationId)) {
			const { status, crewMember, version, id } = assigment;
			segmentMap.set(stationId, {
				...segmentMap.get(stationId),
				status,
				crewMember,
				version,
				id,
			});
		}
	});
	return segmentMap;
};
